/**
 * Utility functions for working with pricing snapshots
 * These functions provide a consistent interface for accessing snapshot data
 * across OfferPage, ComparisonPage, and OffersHistoryPage
 */

import { getEniqTierDisplayName } from './eniqPricing';

/**
 * Product display order - defines the priority for displaying products
 * Secondary products (phone-numbers, voice-traffic) are always shown at the bottom
 */
export const PRODUCT_DISPLAY_ORDER = [
  // Primary products
  'business-phone',
  'fmc',
  'call-tracking',
  'ai-conversation',
  'callback',
  'omnichannel-chat',
  'eniq-ai',
  'voicebot',
  // Secondary products (always at the bottom)
  'phone-numbers',
  'voice-traffic'
];

/**
 * Sort products by display priority
 * @param {Array} products - Array of products or product types
 * @returns {Array} Sorted array
 */
export const sortProductsByPriority = (products) => {
  return [...products].sort((a, b) => {
    const typeA = a.productType || a;
    const typeB = b.productType || b;
    const indexA = PRODUCT_DISPLAY_ORDER.indexOf(typeA);
    const indexB = PRODUCT_DISPLAY_ORDER.indexOf(typeB);

    // If product not in list - push to the end
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
};

/**
 * Get product data from pricing snapshot by product type
 * @param {Object} snapshot - The pricing snapshot object
 * @param {string} productType - The product type to find (e.g., 'business-phone', 'call-tracking')
 * @returns {Object|null} Product data or null if not found
 */
export const getProductFromSnapshot = (snapshot, productType) => {
  if (!snapshot?.individual_prices) return null;
  
  return snapshot.individual_prices.find(
    item => item.productType === productType
  );
};

/**
 * Get all phone numbers from pricing snapshot
 * @param {Object} snapshot - The pricing snapshot object
 * @returns {Array} Array of phone number entries
 */
export const getPhoneNumbersFromSnapshot = (snapshot) => {
  if (!snapshot?.individual_prices) return [];
  
  return snapshot.individual_prices.filter(
    item => item.productType === 'phone-numbers'
  );
};

/**
 * Get all voice traffic entries from pricing snapshot
 * @param {Object} snapshot - The pricing snapshot object
 * @returns {Array} Array of voice traffic entries
 */
export const getVoiceTrafficFromSnapshot = (snapshot) => {
  if (!snapshot?.individual_prices) return [];
  
  return snapshot.individual_prices.filter(
    item => item.productType === 'voice-traffic'
  );
};

/**
 * Check if a product is active in the snapshot
 * @param {Object} snapshot - The pricing snapshot object
 * @param {string} productType - The product type to check
 * @returns {boolean} True if product is active
 */
export const isProductActive = (snapshot, productType) => {
  return !!getProductFromSnapshot(snapshot, productType);
};

/**
 * Get all active product types from multiple snapshots
 * @param {Array<Object>} snapshots - Array of pricing snapshots
 * @returns {Array<string>} Array of unique product types
 */
export const getActiveProductTypes = (snapshots) => {
  const types = new Set();

  if (!Array.isArray(snapshots)) return [];

  snapshots.forEach(snapshot => {
    if (snapshot?.individual_prices) {
      snapshot.individual_prices.forEach(item => {
        if (item.productType) {
          types.add(item.productType);
        }
      });
    }
  });

  // Sort by priority: secondary products (phone-numbers, voice-traffic) at the bottom
  return sortProductsByPriority(Array.from(types));
};

/**
 * Get subscription plan by ID
 * @param {Object} snapshot - The pricing snapshot object
 * @param {string} planId - The plan ID to find
 * @returns {Object|null} Subscription plan or null if not found
 */
export const getSubscriptionPlan = (snapshot, planId) => {
  if (!snapshot?.subscription_plans) return null;
  
  return snapshot.subscription_plans.find(plan => plan.id === planId);
};

/**
 * Get all active subscription types from multiple snapshots
 * @param {Array<Object>} snapshots - Array of pricing snapshots
 * @returns {Array<string>} Array of unique subscription plan IDs
 */
export const getActiveSubscriptionTypes = (snapshots) => {
  const types = new Set();
  
  if (!Array.isArray(snapshots)) return [];
  
  snapshots.forEach(snapshot => {
    if (snapshot?.subscription_plans) {
      snapshot.subscription_plans.forEach(plan => {
        if (plan.id) {
          types.add(plan.id);
        }
      });
    }
  });
  
  return Array.from(types);
};

/**
 * Check if a subscription plan is available in the snapshot
 * @param {Object} snapshot - The pricing snapshot object
 * @param {string} planId - The plan ID to check
 * @returns {boolean} True if plan is available
 */
export const isSubscriptionPlanAvailable = (snapshot, planId) => {
  return !!getSubscriptionPlan(snapshot, planId);
};

/**
 * Get the default/recommended subscription plan
 * @param {Object} snapshot - The pricing snapshot object
 * @returns {Object|null} Default plan or null if not found
 */
export const getDefaultSubscriptionPlan = (snapshot) => {
  if (!snapshot?.subscription_plans) return null;
  
  // First try to find explicitly marked default
  const defaultPlan = snapshot.subscription_plans.find(plan => 
    plan.isDefault || plan.isRecommended
  );
  
  if (defaultPlan) return defaultPlan;
  
  // Fallback to 360-days plan as default
  return snapshot.subscription_plans.find(plan => plan.id === '360-days') || 
         snapshot.subscription_plans[0] || 
         null;
};

/**
 * Format product metadata for display
 * @param {Object} productData - Product data from snapshot
 * @param {string} productType - Product type
 * @param {Function} t - Translation function
 * @param {boolean} compact - Whether to show compact version (for comparison pages)
 * @returns {string} Formatted metadata string
 */
export const formatProductMetadata = (productData, productType, t, compact = false) => {
  if (!productData) return '';
  
  switch (productType) {
    case 'business-phone':
      // Tier is now included in the label, so just show users count
      return productData.users ? `${productData.users} ${t('calculator.businessPhoneUsers', 'users')}` : '';

    case 'fmc':
      // Tier is now included in the label, so just show users count
      return productData.users ? `${productData.users} ${t('calculator.fmcUsers', 'users')}` : '';
    
    case 'call-tracking':
      return productData.slots ? `${productData.slots} ${t('calculator.callTrackingSlots', 'slots')}` : '';
    
    case 'ai-conversation':
      // If included in bundle (price = 0), no extra label — ProductCard shows "Included"
      if (productData.price === 0) {
        return '';
      }
      // Otherwise show users (AI is counted by users, not minutes anymore)
      if (productData.users) {
        return `${productData.users} ${t('calculator.businessPhoneUsers', 'users')}`;
      }
      return '';

    case 'callback':
      // If included in bundle (price = 0), no extra label — ProductCard shows "Included"
      if (productData.price === 0) {
        return '';
      }
      return productData.sessions ?
        `${productData.sessions.toLocaleString()} ${t('calculator.callbackSessions', 'sessions')}` : '';
    
    case 'voice-traffic':
      // Voice Traffic metadata formatting
      
      // Check if this is custom amount mode
      if (productData.useCustomAmount) {
        return t('calculator.voiceTrafficCustomAmountInput', 'Custom amount per month');
      }
      
      // Generate detailed package information if we have pricing data (prioritize over metadata)
      if (productData.priceLocal && productData.currency) {
        const countryCode = productData.countryCode?.toUpperCase() || '';
        // Backend always provides correct package minutes - use it directly
        const minutes = productData.minutes || 0;
        const overageMinutes = productData.overageMinutes || 0;

        // Use actual pricing data from Bundle API
        const pricePerMinuteLocal = productData.pricePerMinuteLocal || 0;
        const overageRateLocal = productData.overageRateLocal || productData.pricePerMinuteLocal || 0;
        
        // Get currency unit translation
        const currencyUnits = {
          'UAH': t('calculator.voiceTrafficCurrencyUAH', 'UAH/min'),
          'KZT': t('calculator.voiceTrafficCurrencyKZT', 'KZT/min'),
          'EUR': t('calculator.voiceTrafficCurrencyEUR', '€/min'),
          'PLN': t('calculator.voiceTrafficCurrencyPLN', 'PLN/min')
        };
        const currency = (productData.currency || '').toUpperCase();
        const currencyUnit = currencyUnits[currency] || `${productData.currency}/min`;
        const minutesUnit = t('calculator.voiceTrafficMinutesShort', 'min.');
        
        let detailLabel = `${minutes} ${minutesUnit} (${pricePerMinuteLocal} ${currencyUnit})`;
        if (overageMinutes > 0) {
          detailLabel += ` + ${overageMinutes} ${t('calculator.voiceTrafficOverageText', 'min. over package')} (${overageRateLocal} ${currencyUnit})`;
        }
        
        return detailLabel;
      }
      
      // Use metadata as fallback if no detailed pricing data
      if (productData.metadata) {
        return productData.metadata;
      }
      
      // Fallback to package name (legacy format)
      if (productData.packageName) {
        return productData.packageName;
      }
      
      // Fallback: show minutes with overage if available
      // Fallback check for minutes fields
      
      if (productData.requestedMinutes && productData.overageMinutes) {
        const baseMinutes = productData.requestedMinutes - productData.overageMinutes;
        return `${baseMinutes.toLocaleString()} ${t('calculator.voiceTrafficMinutes', 'хвилин')} + ${productData.overageMinutes} ${t('calculator.voiceTrafficMinutesShort', 'хв.')} ${t('calculator.voiceTrafficOverageText', 'зверх пакету')}`;
      }
      
      return productData.minutes ? 
        `${productData.minutes.toLocaleString()} ${t('calculator.minute', 'minutes')}` : '';
    
    case 'phone-numbers':
      return productData.count ? 
        `${productData.count} ${t('calculator.phoneNumbersCount', 'numbers')}` : '';
    
    case 'omnichannel-chat':
      // Users come from Business Phone — don't duplicate.
      // ProductCard shows "Included" when price === 0; otherwise leave description empty.
      return '';
    
    case 'eniq-ai':
      // Per-user model: "Standard, 12 users".
      // Checked first, but the per-minute branches below are kept for offers
      // saved under the legacy model — do not remove them.
      if (productData.eniqTier) {
        const eniqTierName = getEniqTierDisplayName(productData.eniqTier);
        const eniqUsers = productData.users || 0;

        if (!eniqUsers) {
          return eniqTierName;
        }
        return `${eniqTierName}, ${eniqUsers} ${t('calculator.businessPhoneUsers', 'users')}`;
      }

      // Helper function to get plan name (keep in English on all languages)
      const getPlanName = (planId) => {
        switch (planId) {
          case 'start': return 'Start';
          case 'pro': return 'Pro';
          case 'max': return 'Max';
          case 'scale': return 'Scale';
          default: return planId || 'Start';
        }
      };
      
      // Choose display format based on compact parameter
      if (productData.selected_plan) {
        const planLabel = getPlanName(productData.selected_plan);
        const totalUnits = productData.total_units || productData.units || 0;
        
        if (compact) {
          // Compact display for comparison pages: just total units and plan
          return `${totalUnits.toLocaleString()} ${t('calculator.eniqAiUnits', 'units')} (${planLabel})`;
        }
        
        // Enhanced display with detailed information from Bundle API for offer pages
        const includedUnits = productData.included_units || 0;
        const extraUnits = productData.extra_units || 0;
        const pricePerUnit = productData.price_per_unit || 0;
        
        // New format: "Тариф Scale 20 000 юнітів включено, +20 000 додаткових юнітів × $0.05"
        let details = `${t('calculator.eniqAiTariff', 'Тариф')} ${planLabel}`;
        
        // Add included units info
        if (includedUnits > 0) {
          details += ` ${includedUnits.toLocaleString()} ${t('calculator.eniqAiUnitsIncluded', 'юнітів включено')}`;
        }
        
        // Add extra units info with price per unit
        if (extraUnits > 0) {
          details += `, +${extraUnits.toLocaleString()} ${t('calculator.eniqAiExtraUnits', 'додаткових юнітів')}`;
          if (pricePerUnit > 0) {
            details += ` × $${pricePerUnit.toFixed(2)}`;
          }
        }
        
        return details;
      }
      
      // Fallback: show minutes if no detailed data available (legacy support)
      if (productData.minutes) {
        return `${productData.minutes.toLocaleString()} ${t('calculator.eniqAiMinutes', 'minutes')}`;
      }
      
      return '';

    case 'voicebot':
      // Helper function to get plan name (keep in English on all languages)
      const getVoiceBotPlanName = (planId) => {
        switch (planId) {
          case 'lite': return 'Lite';
          case 'pro': return 'Pro';
          case 'max': return 'Max';
          case 'scale': return 'Scale';
          default: return planId || 'Lite';
        }
      };

      if (productData.selected_plan) {
        const planLabel = getVoiceBotPlanName(productData.selected_plan);
        const totalUnits = productData.total_units || productData.units || 0;

        if (compact) {
          return `${Math.floor(totalUnits).toLocaleString()} ${t('calculator.voicebotMinutesUnit', 'min')} (${planLabel})`;
        }

        const includedUnits = productData.included_units || 0;
        const extraUnits = productData.extra_units || 0;
        const pricePerUnit = productData.price_per_unit || 0;

        let details = `${t('calculator.tariff', 'Тариф')} ${planLabel}`;

        if (includedUnits > 0) {
          details += ` ${Math.floor(includedUnits).toLocaleString()} ${t('calculator.voicebotMinIncluded', 'min included')}`;
        }

        if (extraUnits > 0) {
          details += `, +${Math.floor(extraUnits).toLocaleString()} ${t('calculator.voicebotExtraMin', 'extra min')}`;
          if (pricePerUnit > 0) {
            // Show up to 3 decimals, strip trailing zeros (0.140 → 0.14, 0.005 → 0.005)
            details += ` × $${parseFloat(pricePerUnit.toFixed(3))}`;
          }
        }

        return details;
      }

      if (productData.minutes) {
        return `${productData.minutes.toLocaleString()} ${t('calculator.voicebotMinutesUnit', 'min')}`;
      }

      return '';

    default:
      return '';
  }
};

const getRegionPriceCurrency = (region) => (region === 'eu' ? 'EUR' : 'USD');

const recoverDroppedPriceCurrency = (snapshot) => {
  if (!snapshot?.individual_prices || !Array.isArray(snapshot.individual_prices)) {
    return snapshot;
  }

  const hasMissingPriceCurrency = snapshot.individual_prices.some(item => !item.priceCurrency);
  if (!hasMissingPriceCurrency) return snapshot;

  const total = Number(snapshot.total_monthly || 0);
  const itemSum = snapshot.individual_prices.reduce((sum, item) => sum + Number(item.price || 0), 0);
  const pricesAlreadyMatchSnapshotTotal = total > 0 && Math.abs(itemSum - total) < 0.01;

  if (!pricesAlreadyMatchSnapshotTotal) return snapshot;

  const priceCurrency = getRegionPriceCurrency(snapshot.region);
  return {
    ...snapshot,
    individual_prices: snapshot.individual_prices.map(item => (
      item.priceCurrency ? item : { ...item, priceCurrency }
    )),
  };
};

/**
 * Get snapshot from offer data (handles different data structures)
 * @param {Object} offer - Offer object
 * @returns {Object|null} Pricing snapshot
 */
export const getSnapshotFromOffer = (offer) => {
  // Priority 1: pricing_snapshot (new structure)
  if (offer?.pricing_snapshot) {
    return recoverDroppedPriceCurrency(offer.pricing_snapshot);
  }
  
  // Priority 2: calculated_pricing (legacy structure)
  if (offer?.calculated_pricing || offer?.calculatedPricing) {
    const calcPricing = offer.calculated_pricing || offer.calculatedPricing;
    
    // If it has summaryItems, convert to snapshot format
    if (calcPricing.summaryItems) {
      const region = calcPricing.region || 'eu';
      // Don't trust legacy currency, derive from region
      const currency = region === 'eu' ? '€' : region === 'ua' ? '$' : '$';
      
      // Collect one-time payments from legacy data (snake_case or camelCase)
      let oneTimePayments = calcPricing.one_time_payments || calcPricing.oneTimePayments || [];
      
      // Fallback: detect activation fees from tab_data (settings may be nested or top-level)
      const tabData = offer?.tab_data || {};
      const phoneSettings = tabData.settings || tabData; // allow both structures
      const activationEnabled = phoneSettings.enablePhoneActivation;
      const phoneNumbersArr = phoneSettings.phoneNumbers || tabData.phoneNumbers;
      if (activationEnabled && Array.isArray(phoneNumbersArr) && phoneNumbersArr.length > 0) {
        const phoneNumbers = phoneNumbersArr || [];
        let totalActivation = 0;
        
        phoneNumbers.forEach(phone => {
          if (phone.quantity > 0 && phone.activationFee > 0) {
            totalActivation += phone.quantity * phone.activationFee;
          }
        });
        
        if (totalActivation > 0) {
          oneTimePayments = [{
            total: totalActivation,
            hasActivation: true,
            description: 'Phone numbers activation'
          }];
        }
      }
      
      // one_time_payments prepared from legacy data if present
      
      return {
        individual_prices: calcPricing.summaryItems,
        subscription_plans: calcPricing.subscriptionPlans || [],
        total_monthly: calcPricing.totalMonthly || 0,
        one_time_payments: oneTimePayments,
        currency: currency,
        region: region
      };
    }
  }
  
  // No snapshot data available
  return null;
};

/**
 * Get product icon mapping
 * @param {string} type - Product type
 * @returns {string} Icon component name
 */
export const getProductIconName = (type) => {
  const icons = {
    'business-phone': 'Phonelink',
    'fmc': 'Phonelink',
    'call-tracking': 'AdsClick',
    'ai-conversation': 'Insights',
    'callback': 'CallToAction',
    'phone-numbers': 'LocalPhone',
    'voice-traffic': 'PhoneInTalk',
    'eniq-ai': 'SmartToy'
  };
  return icons[type] || 'Phonelink';
};
